import { Box, Checkbox, Radio, TableCell, TableRow } from "@mui/material";
import { ReactComponent as DragIndicatorIcon } from "components/Icons/drag-indicator.svg";
import { useMessage } from "hooks/intl";
import { useDrag, useDrop } from "react-dnd";
import { RowData } from "types/settings";
import { useStyles } from "./MembersConfiguration.styles";

interface DraggableTableRowProps {
  row: RowData;
  priority: number;
  moveRow: (dragIndex: number, hoverIndex: number) => void;
  onRequiredChange: (id: string) => void;
  onEventMatchingChange: (id: string, value: boolean) => void;
  onUniqueChange: (id: string, value: boolean) => void;
  // Saved data - initial API values
  settingsData?: RowData[];
  // All current rows
  rows: RowData[];
}

export function DraggableTableRow({
  row,
  priority,
  moveRow,
  onRequiredChange,
  onEventMatchingChange,
  onUniqueChange,
  settingsData,
  rows,
}: DraggableTableRowProps) {
  const classes = useStyles();
  const { message } = useMessage();

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: "row",
    item: { index: priority, row },
    canDrag: () => row.eventMatching,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "row",
    hover: (dragItem: { index: number }) => {
      // Find first row with event matching disabled
      const firstNonEventMatchingIndex = rows.findIndex(
        (row) => !row.eventMatching,
      );

      // Prevent dropping rows below non-event matching rows
      if (
        firstNonEventMatchingIndex !== -1 &&
        priority >= firstNonEventMatchingIndex
      ) {
        return;
      }

      // Prevent dragging non-event matching rows
      const draggedRow = rows[dragItem.index];
      if (!draggedRow.eventMatching) {
        return;
      }

      if (dragItem.index !== priority) {
        moveRow(dragItem.index, priority);
        dragItem.index = priority;
      }
    },
  });

  const savedConfiguration = settingsData?.find(
    (config: RowData) => config.field === row.field,
  );

  const wasUniquePreviouslyFalse =
    savedConfiguration && !savedConfiguration.unique;
  const isNotUnique = !row.unique;
  const isUniqueFieldDisabled =
    row.eventMatching ||
    row.identificationMethod ||
    (wasUniquePreviouslyFalse && !row.unique);

  return (
    <TableRow
      ref={(node) => dragPreview(drop(node))}
      className={`${classes.tableRow} ${isDragging ? "isDragging" : ""}`}
    >
      <TableCell
        ref={row.eventMatching ? drag : null}
        className={`${classes.dragCell} ${!row.eventMatching ? "disabled" : ""}`}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {row.eventMatching && (
            <DragIndicatorIcon className={classes.dragIcon} />
          )}
          {message({ id: row.field })}
        </Box>
      </TableCell>
      <TableCell
        align="center"
        className={`${classes.cell} ${isNotUnique ? classes.disabledCell : ""}`}
      >
        <Radio
          checked={row.identificationMethod}
          onChange={() => onRequiredChange(row.field)}
          size="small"
          disabled={isNotUnique}
          className={classes.radio}
          data-test-id={`registration-method-option-${row.field}`}
        />
      </TableCell>
      <TableCell
        align="center"
        className={`${classes.cell} ${isNotUnique ? classes.disabledCell : ""}`}
      >
        <Checkbox
          checked={row.eventMatching}
          disabled={isNotUnique}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onEventMatchingChange(row.field, e.target.checked)
          }
          size="small"
          className={classes.checkbox}
          data-test-id={`members-config-event-matching-checkbox-${row.field}`}
        />
      </TableCell>
      <TableCell
        align="center"
        className={`${classes.cell} ${isUniqueFieldDisabled ? classes.disabledCell : ""}`}
      >
        <Checkbox
          checked={row.unique}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onUniqueChange(row.field, e.target.checked)
          }
          size="small"
          disabled={isUniqueFieldDisabled}
          className={classes.checkbox}
          data-test-id={`members-config-unique-checkbox-${row.field}`}
        />
      </TableCell>
    </TableRow>
  );
}
